import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Paper, Typography } from "@mui/material";
import { useChartPalette } from "../utils/chartUtils";
import { EmptyChartState } from "@/components/common/EmptyChartState";
import { formatUSD } from "@/utils/formatters/currencyFormatter";

type DiagnosisPoint = {
  icd_code: string;
  description: string;
  claim_item_count: number;
  avg_paid: number;
  total_paid: number;
};

type Props = {
  data: DiagnosisPoint[];
  isFullscreen: boolean;
};

const DiagnosisTooltip = ({ active, payload }: { active?: boolean; payload?: { payload: DiagnosisPoint }[] }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <Paper elevation={3} sx={{ p: 1.5, maxWidth: 280 }}>
      <Typography variant="subtitle2">{p.icd_code}</Typography>
      {p.description && (
        <Typography variant="caption" color="text.secondary" display="block">
          {p.description}
        </Typography>
      )}
      <Typography variant="body2" sx={{ mt: 0.5 }}>
        Avg paid / line: <strong>{formatUSD(p.avg_paid)}</strong>
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {p.claim_item_count.toLocaleString()} lines · total {formatUSD(p.total_paid)}
      </Typography>
    </Paper>
  );
};

/**
 * Average paid per claim line by ICD-10 code, rendered as an area curve with the
 * codes sorted by avg paid (Pareto-style). Coded ICD is sparse — codes without a
 * value are excluded upstream.
 */
const DiagnosisAreaChart = ({ data, isFullscreen }: Props) => {
  const palette = useChartPalette();
  if (!data.length) return <EmptyChartState />;

  return (
    <ResponsiveContainer width="100%" height={isFullscreen ? 500 : "100%"}>
      <AreaChart data={data} margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
        <defs>
          <linearGradient id="diagnosisFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={palette[0]} stopOpacity={0.6} />
            <stop offset="95%" stopColor={palette[0]} stopOpacity={0.05} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--mui-palette-divider, rgba(0,0,0,0.08))" />
        <XAxis
          dataKey="icd_code"
          angle={-30}
          textAnchor="end"
          interval={0}
          height={60}
          tick={{ fontSize: 12 }}
          axisLine={false}
          tickLine={false}
        />
        <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12 }} tickFormatter={(v: number) => formatUSD(v)} />
        <Tooltip content={<DiagnosisTooltip />} />
        <Area type="monotone" dataKey="avg_paid" stroke={palette[0]} strokeWidth={2} fill="url(#diagnosisFill)" />
      </AreaChart>
    </ResponsiveContainer>
  );
};

export default DiagnosisAreaChart;
